import React, { useState, useEffect } from "react";
import { URL } from "../config";
import { useNavigate } from "react-router-dom";

const BR = "#1D4ED8", DK = "#072877";

if (typeof document !== "undefined" && !document.getElementById("poppins-font")) {
  const link = document.createElement("link");
  link.id = "poppins-font";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

const AVATAR_COLORS = [
  "#1D4ED8","#7C3AED","#DB2777","#D97706","#059669",
  "#DC2626","#2563EB","#9333EA","#EA580C","#0891B2",
];
const getAvatarColor = name => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = name => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getStatusConfig = (statusName) => {
  if (!statusName) return { bg: "#F5F3FF", color: "#6D28D9", icon: "🆕", label: "New" };
  const n = statusName.toLowerCase();
  if (n.includes("follow"))  return { bg: "#FEF9C3", color: "#854D0E", icon: "🔁",  label: statusName };
  if (n.includes("success")) return { bg: "#DCFCE7", color: "#166534", icon: "✅",  label: statusName };
  if (n.includes("cancel"))  return { bg: "#FEE2E2", color: "#991B1B", icon: "❌",  label: statusName };
  return { bg: "#F5F3FF", color: "#6D28D9", icon: "🆕", label: statusName };
};

function Avatar({ name, size = 34 }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: getAvatarColor(name), color: "#fff",
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0, lineHeight: 1,
      fontFamily: "'Poppins', sans-serif",
    }}>
      {getInitials(name)}
    </span>
  );
}

function StatusBadge({ statusName }) {
  const cfg = getStatusConfig(statusName);
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 20, fontSize: 12,
      fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: "'Poppins', sans-serif", whiteSpace: "nowrap",
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none", color: "#94A3B8" }}>🔍</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", height: 40, border: "1.5px solid #E2E8F0", borderRadius: 8,
          padding: "0 12px 0 38px", fontSize: 13.5, fontFamily: "'Poppins', sans-serif",
          color: "#1E293B", outline: "none", boxSizing: "border-box", background: "#fff",
        }}
        onFocus={e => e.target.style.borderColor = BR}
        onBlur={e => e.target.style.borderColor = "#E2E8F0"}
      />
    </div>
  );
}

function Skeleton({ width = "100%", height = 16, radius = 6, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: "linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)",
      backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", ...style,
    }} />
  );
}

// ─── Enquiry Detail Modal ─────────────────────────────────────────────────────
function EnquiryModal({ enquiry, onClose, empMap, lsMap }) {
  if (!enquiry) return null;
  const c = enquiry.customer || {};
  const latestFollowup = enquiry.followups?.[0] || null;
  const statusName = latestFollowup?.fkLeadStatusId ? (lsMap[latestFollowup.fkLeadStatusId] || "—") : "New";
  const assignName = latestFollowup?.assignTo ? (empMap[latestFollowup.assignTo] || `Emp #${latestFollowup.assignTo}`) : null;

  const rows = [
    { label: "Enquiry ID",    value: enquiry.enquiryId },
    { label: "Enquiry Date",  value: enquiry.enquiryDate ? new Date(enquiry.enquiryDate).toLocaleDateString("en-IN") : "—" },
    { label: "Customer",      value: c.customerName || "—" },
    { label: "Mobile",        value: c.mobileno || "—" },
    { label: "Email",         value: c.emailid || "—" },
    { label: "City",          value: c.city || "—" },
    { label: "Source",        value: enquiry.source?.sourceName || "—" },
    { label: "Property Type", value: enquiry.propertyType?.propertytypeName || "—" },
    { label: "Budget",        value: enquiry.budget?.budgetName || "—" },
    { label: "Primary Goal",  value: enquiry.primaryGoal?.primarygoalName || "—" },
    { label: "Timeline",      value: enquiry.timeline?.timelineName || "—" },
    { label: "Employee Ref.", value: enquiry.employee?.employeeName || "—" },
    { label: "Comm. Media",   value: enquiry.communicationMedia?.communicationmediaName || "—" },
    { label: "Areas",         value: enquiry.areas?.map(a => a.areaName).join(", ") || "—" },
    { label: "Note",          value: enquiry.note || "—" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(7,40,119,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 640, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px rgba(7,40,119,.2)", display: "flex", flexDirection: "column", maxHeight: "90vh", fontFamily: "'Poppins', sans-serif" }}>
        <div style={{ background: `linear-gradient(120deg,${DK},${BR})`, padding: "16px 20px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar name={c.customerName} size={40} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{c.customerName || "Unknown"}</div>
              <div style={{ fontSize: 12, opacity: .75 }}>Enquiry #{enquiry.enquiryId}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "8px 10px 8px 0", fontWeight: 600, color: "#64748B", whiteSpace: "nowrap", width: "38%", verticalAlign: "top" }}>{r.label}</td>
                  <td style={{ padding: "8px 0", color: "#1E293B", wordBreak: "break-word" }}>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {latestFollowup && (
            <div style={{ background: "#EFF6FF", borderLeft: `4px solid ${BR}`, borderRadius: "0 8px 8px 0", padding: "12px 16px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: BR, marginBottom: 8 }}>Latest Follow-up</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 13, color: "#475569" }}>
                <span><strong>Status:</strong> <StatusBadge statusName={statusName} /></span>
                {assignName && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <strong>Assigned To:</strong>
                    <Avatar name={assignName} size={20} />
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>{assignName}</span>
                  </span>
                )}
                {latestFollowup.callLeadDate && <span><strong>Follow-up Date:</strong> {new Date(latestFollowup.callLeadDate).toLocaleDateString("en-IN")}</span>}
                {latestFollowup.callLeadTime && <span><strong>Time:</strong> {latestFollowup.callLeadTime}</span>}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: 50, border: "none", background: BR, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const InquiryDetails = () => {
  const navigate = useNavigate();

  // ✅ Login user localStorage मधून घ्या
  const storedUser   = JSON.parse(localStorage.getItem('user_data') || '{}');
  const loggedUserId = storedUser?.userid;
  const loggedRole   = storedUser?.role;   // 1 = Admin, बाकी = Employee
  const loggedName   = storedUser?.username;

  const [enquiries, setEnquiries]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState("");
  const [statusFilter, setStatusFilter]     = useState("All");
  const [propertyFilter, setPropertyFilter] = useState("All");
  const [selected, setSelected]             = useState(null);
  const [page, setPage]                     = useState(1);

  const [empMap, setEmpMap] = useState({});
  const [lsMap,  setLsMap]  = useState({});

  const PER_PAGE = 10;

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const af = encodeURIComponent(JSON.stringify({ where: { isActive: 1 } }));
        const efFields = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, fields: ["employeeId","employeeName"] }));

        const [res, followupRes, empRes, lsRes] = await Promise.all([
          fetch(`${URL}/enquiries?filter=${encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, order: ["enquiryId DESC"] }))}`),
          fetch(`${URL}/followup-masters?filter=${encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, order: ["followupId DESC"] }))}`),
          fetch(`${URL}/employee-masters?filter=${efFields}`),
          fetch(`${URL}/lead-status-masters?filter=${af}`),
        ]);

        if (!res.ok) throw new Error(`Server error ${res.status}`);

        const [data, followupData, empData, lsData] = await Promise.all([
          res.json(),
          followupRes.ok ? followupRes.json() : [],
          empRes.ok ? empRes.json() : [],
          lsRes.ok  ? lsRes.json()  : [],
        ]);

        const eMap = {};
        empData.forEach(e => { eMap[e.employeeId] = e.employeeName; });
        setEmpMap(eMap);

        const lMap = {};
        lsData.forEach(l => { lMap[l.statusId] = l.statusName; });
        setLsMap(lMap);

        const withFollowups = data.map(e => ({
          ...e,
          followups: followupData.filter(f => f.fkenquiryId === e.enquiryId),
        }));

        setEnquiries(withFollowups);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleEdit = (ev, enquiry) => {
    ev.stopPropagation();
    navigate("/inquiry", { state: { editEnquiry: enquiry } });
  };

  const propertyTypes = [
    ...new Map(
      enquiries
        .filter(e => e.propertyType?.propertytypeName)
        .map(e => [e.propertyType.propertytypeId, e.propertyType])
    ).values()
  ];

  const filtered = enquiries.filter(e => {
    const name     = e.customer?.customerName         ? String(e.customer.customerName).toLowerCase()          : "";
    const mobile   = e.customer?.mobileno             ? String(e.customer.mobileno).toLowerCase()              : "";
    const source   = e.source?.sourceName             ? String(e.source.sourceName).toLowerCase()              : "";
    const prop     = e.propertyType?.propertytypeName ? String(e.propertyType.propertytypeName).toLowerCase()  : "";
    const followup = e.followups?.[0];
    const assignId = followup?.assignTo;
    const assignName = assignId ? (empMap[assignId] || "").toLowerCase() : "";
    const q = search.toLowerCase();

    const matchQ = !q || name.includes(q) || mobile.includes(q) || source.includes(q) || prop.includes(q) || String(e.enquiryId).includes(q) || assignName.includes(q);

    const statusId   = followup?.fkLeadStatusId;
    const statusName = statusId ? (lsMap[statusId] || "") : "";
    const sn         = statusName.toLowerCase();

    const matchS =
      statusFilter === "All" ||
      (statusFilter === "New"           && (!followup || !statusId)) ||
      (statusFilter === "Follow"        && sn.includes("follow")) ||
      (statusFilter === "Success Close" && sn.includes("success")) ||
      (statusFilter === "Cancel"        && sn.includes("cancel"));

    const matchP =
      propertyFilter === "All" ||
      (e.propertyType?.propertytypeName ? String(e.propertyType.propertytypeName) === propertyFilter : false);

    // ✅ FIXED: createdBy field वापरून filter — जो user ने enquiry create केली तोच बघू शकतो
    // Admin (role === 1) → सगळे leads दिसतात
    // Employee → फक्त त्याने CREATE केलेल्या enquiries (createdBy नुसार)
    const matchRole = loggedRole === 1 || String(e.createdBy) === String(loggedUserId);

    return matchQ && matchS && matchP && matchRole;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const statusOptions = ["All", "New", "Follow", "Success Close", "Cancel"];

  useEffect(() => { setPage(1); }, [search, statusFilter, propertyFilter]);

  const formatDate = d => d
    ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  // ✅ FIXED: Stats पण createdBy नुसार filter
  const visibleEnquiries = loggedRole === 1
    ? enquiries
    : enquiries.filter(e => String(e.createdBy) === String(loggedUserId));

  const statsFollow  = visibleEnquiries.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("follow"); }).length;
  const statsSuccess = visibleEnquiries.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("success"); }).length;
  const statsCancel  = visibleEnquiries.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("cancel"); }).length;
  const statsNew     = visibleEnquiries.length - statsFollow - statsSuccess - statsCancel;

  const isFilterActive = search || statusFilter !== "All" || propertyFilter !== "All";

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: "#F1F5F9", fontFamily: "'Poppins', sans-serif", padding: "0 0 40px" }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .enq-row:hover { background: #F8FAFC !important; }
        .enq-row { transition: background .15s; cursor: pointer; }
        .edit-btn { padding: 5px 13px; border-radius: 20px; border: 1.5px solid #E2E8F0; background: #fff; color: #64748B; font-size: 12px; font-weight: 600; cursor: pointer; font-family: 'Poppins', sans-serif; display: inline-flex; align-items: center; gap: 4px; transition: all .15s; white-space: nowrap; }
        .edit-btn:hover { background: #EFF6FF !important; color: #1D4ED8 !important; border-color: #1D4ED8 !important; }
        @media (max-width: 640px) {
          .filter-bar { flex-direction: column !important; }
          .status-pills { flex-wrap: wrap; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background: "#0d1b3e",
        backgroundImage: `repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px)`,
        padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, minHeight: 76, fontFamily: "'Poppins', sans-serif",
      }}>
        <div style={{ padding: "14px 0" }}>
          <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "rgba(148,163,184,0.65)", marginBottom: 3 }}>LEAD MANAGEMENT</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>📋 Inquiry Details</div>
        </div>

        {/* ✅ Header मध्ये logged-in user दाखवा */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: "rgba(255,255,255,0.08)", borderRadius: 50, border: "1px solid rgba(255,255,255,0.12)" }}>
            <Avatar name={loggedName} size={32} />
            <div>
              <div style={{ fontSize: 10, color: "rgba(148,163,184,0.8)", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                {loggedRole === 1 ? "👑 Admin" : "👤 Employee"}
              </div>
              <div style={{ fontSize: 13, color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
                {loggedName || "—"}
              </div>
            </div>
          </div>

          <button onClick={() => navigate("/inquiry")} style={{ background: "#fff", color: BR, border: "none", padding: "9px 20px", borderRadius: 50, fontWeight: 700, fontSize: 13.5, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Poppins', sans-serif", boxShadow: "0 4px 12px rgba(0,0,0,.18)", flexShrink: 0 }}>
            ➕ New Enquiry
          </button>
        </div>
      </div>

      <div style={{ padding: "18px 16px" }}>

        {/* ✅ Employee असल्यास — "Showing your created leads" banner */}
        {!loading && !error && loggedRole !== 1 && (
          <div style={{ background: "#EFF6FF", border: `1.5px solid ${BR}`, borderRadius: 10, padding: "10px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: BR, fontWeight: 600 }}>
            <Avatar name={loggedName} size={28} />
            <span>Showing leads created by <strong>{loggedName}</strong> only</span>
            <span style={{ marginLeft: "auto", background: BR, color: "#fff", padding: "2px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
              {visibleEnquiries.length} Lead{visibleEnquiries.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* ── Stats ── */}
        {!loading && !error && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 18 }}>
            {[
              { label: "Total Enquiries", value: visibleEnquiries.length, icon: "📋", bg: "#EFF6FF", color: BR },
              { label: "New / Pending",   value: statsNew,                icon: "🆕", bg: "#F5F3FF", color: "#6D28D9" },
              { label: "Follow Up",       value: statsFollow,             icon: "🔁", bg: "#FEF9C3", color: "#854D0E" },
              { label: "Success Close",   value: statsSuccess,            icon: "✅", bg: "#DCFCE7", color: "#166534" },
              { label: "Cancelled",       value: statsCancel,             icon: "❌", bg: "#FEE2E2", color: "#991B1B" },
            ].map((c, i) => (
              <div key={i} style={{ background: c.bg, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
                <span style={{ fontSize: 24 }}>{c.icon}</span>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value}</div>
                  <div style={{ fontSize: 11.5, color: "#64748B", fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Filters ── */}
        <div className="filter-bar" style={{ background: "#fff", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", boxShadow: "0 1px 6px rgba(7,40,119,.06)" }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search by name, mobile, source, assigned to..." />

          <select value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)} style={{ height: 40, border: `1.5px solid ${propertyFilter !== "All" ? BR : "#E2E8F0"}`, borderRadius: 8, padding: "0 12px", fontSize: 13.5, fontFamily: "'Poppins', sans-serif", color: propertyFilter === "All" ? "#94A3B8" : "#1E293B", outline: "none", background: propertyFilter !== "All" ? "#EFF6FF" : "#fff", cursor: "pointer", minWidth: 170, fontWeight: propertyFilter !== "All" ? 600 : 400 }}>
            <option value="All">🏠 All Property Types</option>
            {propertyTypes.map(p => (<option key={p.propertytypeId} value={p.propertytypeName}>{p.propertytypeName}</option>))}
          </select>

          <div className="status-pills" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {statusOptions.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: "1.5px solid", fontFamily: "'Poppins', sans-serif", borderColor: statusFilter === s ? BR : "#E2E8F0", background: statusFilter === s ? BR : "#fff", color: statusFilter === s ? "#fff" : "#64748B", transition: "all .15s" }}>
                {s === "Follow" ? "🔁 Follow Up" : s === "Success Close" ? "✅ Success" : s === "Cancel" ? "❌ Cancelled" : s === "New" ? "🆕 New" : "📋 All"}
              </button>
            ))}
          </div>

          {isFilterActive && (
            <button onClick={() => { setSearch(""); setStatusFilter("All"); setPropertyFilter("All"); }} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: "1.5px solid #E2E8F0", background: "#FEE2E2", color: "#991B1B", fontFamily: "'Poppins', sans-serif" }}>✕ Clear</button>
          )}

          <span style={{ marginLeft: "auto", fontSize: 12.5, color: "#64748B", fontWeight: 600, whiteSpace: "nowrap" }}>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 6px rgba(7,40,119,.06)" }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
                <Skeleton width={40} height={40} radius={50} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <Skeleton width="40%" height={14} />
                  <Skeleton width="60%" height={12} />
                </div>
                <Skeleton width={80} height={26} radius={20} />
              </div>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "20px 24px", textAlign: "center", color: "#991B1B" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Failed to load enquiries</div>
            <div style={{ fontSize: 13, opacity: .8, marginBottom: 12 }}>{error}</div>
            <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", borderRadius: 50, background: "#DC2626", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>🔄 Retry</button>
          </div>
        )}

        {/* ── Table ── */}
        {!loading && !error && (
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(7,40,119,.07)", overflow: "hidden", animation: "fadeIn .3s ease" }}>
            {paginated.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: "#94A3B8" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#64748B" }}>No enquiries found</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Try a different search or filter</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                      {["#", "Customer", "Date", "Source", "Property", "Budget", "Areas", "Lead Status", "Assigned To", "Note", "Action"].map(h => (
                        <th key={h} style={{ padding: "11px 12px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#64748B", letterSpacing: "0.5px", textTransform: "uppercase", whiteSpace: "nowrap", fontFamily: "'Poppins', sans-serif" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((e) => {
                      const c        = e.customer || {};
                      const areas    = e.areas?.map(a => a.areaName).join(", ") || "—";
                      const followup = e.followups?.[0] || null;
                      const statusId   = followup?.fkLeadStatusId;
                      const statusName = statusId ? (lsMap[statusId] || "New") : "New";
                      const assignId   = followup?.assignTo;
                      const assignName = assignId ? (empMap[assignId] || `Emp #${assignId}`) : null;

                      return (
                        <tr key={e.enquiryId} className="enq-row" onClick={() => setSelected(e)} style={{ borderBottom: "1px solid #F1F5F9" }}>

                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8", fontWeight: 600 }}>{e.enquiryId}</td>

                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={c.customerName} size={32} />
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1E293B" }}>{c.customerName || "—"}</div>
                                <div style={{ fontSize: 11.5, color: "#94A3B8" }}>{c.mobileno || ""}</div>
                              </div>
                            </div>
                          </td>

                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>{formatDate(e.enquiryDate)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569" }}>{e.source?.sourceName || "—"}</td>

                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569" }}>
                            {e.propertyType?.propertytypeName ? (
                              <span style={{ background: "#EFF6FF", color: BR, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{e.propertyType.propertytypeName}</span>
                            ) : "—"}
                          </td>

                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>{e.budget?.budgetName || "—"}</td>

                          <td style={{ padding: "10px 12px", fontSize: 12.5, color: "#475569", maxWidth: 120 }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={areas}>{areas}</div>
                          </td>

                          <td style={{ padding: "10px 12px" }}>
                            {followup
                              ? <StatusBadge statusName={statusName} />
                              : <StatusBadge statusName="New" />
                            }
                          </td>

                          <td style={{ padding: "10px 12px", fontSize: 13 }}>
                            {assignName ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Avatar name={assignName} size={26} />
                                <div style={{ fontWeight: 600, color: "#1E293B", fontSize: 12.5, whiteSpace: "nowrap" }}>{assignName}</div>
                              </div>
                            ) : (
                              <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>
                            )}
                          </td>

                          <td style={{ padding: "10px 12px", fontSize: 12.5, color: "#64748B", maxWidth: 120 }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.note}>{e.note || "—"}</div>
                          </td>

                          <td style={{ padding: "10px 12px" }} onClick={ev => ev.stopPropagation()}>
                            <button className="edit-btn" onClick={ev => handleEdit(ev, e)}>✏️ Edit</button>
                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ borderTop: "1px solid #F1F5F9", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#64748B" }}>Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: "6px 14px", borderRadius: 6, border: "1.5px solid #E2E8F0", background: page === 1 ? "#F8FAFC" : "#fff", cursor: page === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, color: page === 1 ? "#CBD5E1" : BR, fontFamily: "'Poppins', sans-serif" }}>← Prev</button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)} style={{ padding: "6px 12px", borderRadius: 6, border: "1.5px solid", borderColor: page === i + 1 ? BR : "#E2E8F0", background: page === i + 1 ? BR : "#fff", color: page === i + 1 ? "#fff" : "#64748B", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Poppins', sans-serif", display: Math.abs(page - (i + 1)) > 2 ? "none" : "block" }}>{i + 1}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: "6px 14px", borderRadius: 6, border: "1.5px solid #E2E8F0", background: page === totalPages ? "#F8FAFC" : "#fff", cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, color: page === totalPages ? "#CBD5E1" : BR, fontFamily: "'Poppins', sans-serif" }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selected && <EnquiryModal enquiry={selected} onClose={() => setSelected(null)} empMap={empMap} lsMap={lsMap} />}
    </div>
  );
};

export default InquiryDetails;
